<!-- My Street. The player's own street scene, and the ten decorations they can
     unlock by completing daily challenges. Tap an unlocked one to place it in
     the next free slot; tap a placed one to remove it. -->
<template>
  <IonPage>
    <AppHeader title="My Street" back="/" />

    <IonContent :fullscreen="true">
      <div class="soc-hero mystreet-hero" aria-hidden="true">
        <canvas ref="sceneEl" />
      </div>

      <div class="soc-stack mystreet-stack">
        <p v-if="progress.unlockedDecorations.length === 0" class="soc-muted ion-text-center">
          Complete a Today's Street to unlock your first decoration.
        </p>

        <h2 class="section-title first">Decorations</h2>

        <div class="deco-grid" role="list">
          <button
            v-for="(d, i) in decorations"
            :key="d.id"
            type="button"
            role="listitem"
            class="deco"
            :class="{ 'deco--locked': !d.unlocked, 'deco--placed': d.placedSlot !== null }"
            :aria-label="decoLabel(d)"
            @click="toggleDeco(d)"
          >
            <canvas v-if="d.unlocked" :ref="(el) => setDecoRef(el, i)" width="48" height="48" aria-hidden="true"></canvas>
            <IonIcon v-else :icon="lockClosedOutline" aria-hidden="true" />
            <div>
              <strong>{{ d.name }}</strong>
              <span v-if="d.unlocked">{{ d.placedSlot !== null ? 'Placed on your street' : d.blurb }}</span>
              <span v-else>Unlocks on day {{ d.unlockDay }} — {{ d.blurb }}</span>
            </div>
          </button>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import { IonPage, IonContent, IonIcon, onIonViewWillEnter, onIonViewDidLeave } from '@ionic/vue';
import { lockClosedOutline } from 'ionicons/icons';
import { AppHeader } from '../core/ui';
import { progress, settings, persistProgress, reducedMotion } from '../store.js';
import { DECORATIONS } from '../lib/daily.js';
import { placeDecoration } from '../lib/progress.js';
import { createRenderer } from '../lib/renderer.js';
import { drawDecoration } from '../lib/sprites.js';
import { palette } from '../lib/palette.js';

const sceneEl = ref(null);

function placedSlotFor(id) {
  const found = Object.entries(progress.placedDecorations).find(([, v]) => v === id);
  return found ? Number(found[0]) : null;
}

const decorations = computed(() => DECORATIONS.map((d) => ({
  ...d,
  unlocked: progress.unlockedDecorations.includes(d.id),
  placedSlot: placedSlotFor(d.id),
})));

function decoLabel(d) {
  if (!d.unlocked) return `${d.name}, locked, unlocks on day ${d.unlockDay}`;
  return d.placedSlot !== null ? `${d.name}, placed on your street, tap to remove` : `${d.name}, tap to place on your street`;
}

function nextFreeSlot() {
  for (let i = 0; i < 10; i++) {
    if (!progress.placedDecorations[String(i)]) return i;
  }
  return null;
}

function toggleDeco(d) {
  if (!d.unlocked) return;
  if (d.placedSlot !== null) {
    placeDecoration(progress, d.placedSlot, null);
  } else {
    const slot = nextFreeSlot();
    if (slot === null) return;
    placeDecoration(progress, slot, d.id);
  }
  persistProgress();
  redraw();
  nextTick(drawIcons);
}

// --- decoration list icons -------------------------------------------------
const decoRefs = ref([]);
function setDecoRef(el, i) {
  decoRefs.value[i] = el || null;
}

function drawIcons() {
  decorations.value.forEach((d, i) => {
    const c = decoRefs.value[i];
    if (!c || !d.unlocked) return;
    const ctx = c.getContext('2d');
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    c.width = 48 * dpr; c.height = 48 * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, 48, 48);
    drawDecoration(ctx, palette(), d.id, 24, 24, 34, 0);
  });
}

// --- the street scene -------------------------------------------------------
let renderer = null;
let raf = 0;
let ro = null;

function redraw() {
  if (!renderer) return;
  renderer.drawMyStreet(progress.placedDecorations, window.performance.now());
}

function loop(t) {
  if (renderer) renderer.drawMyStreet(progress.placedDecorations, t);
  if (reducedMotion()) { raf = 0; setTimeout(() => { if (renderer) raf = requestAnimationFrame(loop); }, 1000); return; }
  raf = requestAnimationFrame(loop);
}

function startScene() {
  if (!sceneEl.value) return;
  if (!renderer) {
    renderer = createRenderer(sceneEl.value, { highContrast: settings.highContrast, reducedMotion: reducedMotion() });
    ro = new ResizeObserver(() => { renderer?.resize(); redraw(); });
    ro.observe(sceneEl.value);
  }
  renderer.setOptions({ highContrast: settings.highContrast, reducedMotion: reducedMotion() });
  renderer.resize();
  if (!raf) raf = requestAnimationFrame(loop);
}

function stopScene() {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
}

watch(() => [settings.highContrast, settings.reducedMotion], () => renderer?.setOptions({ highContrast: settings.highContrast, reducedMotion: reducedMotion() }));

onMounted(() => {
  startScene();
  nextTick(drawIcons);
});
onIonViewWillEnter(() => {
  startScene();
  nextTick(drawIcons);
});
onIonViewDidLeave(stopScene);
onBeforeUnmount(() => {
  stopScene();
  ro?.disconnect();
  renderer?.destroy();
  renderer = null;
});
</script>

<style scoped>
.mystreet-hero {
  aspect-ratio: 390 / 260;
  max-width: 34rem;
  margin: 0 auto;
}

.mystreet-stack {
  padding-top: var(--sp-4);
}

.section-title.first {
  margin-top: 0;
}
</style>
